/**
 * FeedbackModal - In-app feedback and support request modal
 *
 * Allows users to:
 * - Report bugs, request features, ask questions
 * - Upload screenshots
 * - Provide contact details
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  MessageCircle,
  Bug,
  Lightbulb,
  HelpCircle,
  MoreHorizontal,
  Upload,
  X,
  Loader2,
  CheckCircle,
  Image as ImageIcon,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/language';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';
import { cn } from '@/lib/utils';

interface FeedbackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type FeedbackType = 'bug' | 'feature' | 'question' | 'other';

const feedbackTypeConfig: Record<FeedbackType, { icon: typeof Bug; label: string; labelEn: string; color: string }> = {
  bug: { icon: Bug, label: 'Signaler un bug', labelEn: 'Report a bug', color: 'text-red-500' },
  feature: { icon: Lightbulb, label: 'Suggérer une fonctionnalité', labelEn: 'Suggest a feature', color: 'text-yellow-500' },
  question: { icon: HelpCircle, label: 'Poser une question', labelEn: 'Ask a question', color: 'text-blue-500' },
  other: { icon: MoreHorizontal, label: 'Autre', labelEn: 'Other', color: 'text-gray-500' },
};

export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  open,
  onOpenChange,
}) => {
  const { user } = useAuth();
  const { language, t } = useLanguage();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [feedbackType, setFeedbackType] = useState<FeedbackType>('bug');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [contactEmail, setContactEmail] = useState(user?.email || '');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Pre-populate contact name and phone from user metadata when modal opens
  useEffect(() => {
    if (!user || !open) return;

    // Profile data is stored in user.user_metadata (Supabase Auth)
    const metadata = user.user_metadata || {};

    const fullName = [metadata.first_name, metadata.last_name]
      .filter(Boolean)
      .join(' ');

    if (fullName && !contactName) {
      setContactName(fullName);
    }
    if (metadata.phone_number && !contactPhone) {
      setContactPhone(metadata.phone_number);
    }
  }, [user, open]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error(language === 'fr' ? 'Veuillez sélectionner une image' : 'Please select an image');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(language === 'fr' ? 'L\'image ne doit pas dépasser 5 Mo' : 'Image must be under 5MB');
        return;
      }

      setScreenshot(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshotPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(language === 'fr' ? 'L\'image ne doit pas dépasser 5 Mo' : 'Image must be under 5MB');
        return;
      }
      setScreenshot(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshotPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeScreenshot = () => {
    setScreenshot(null);
    setScreenshotPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const resetForm = () => {
    setFeedbackType('bug');
    setSubject('');
    setMessage('');
    setContactEmail(user?.email || '');
    setContactName('');
    setContactPhone('');
    setScreenshot(null);
    setScreenshotPreview(null);
    setSuccess(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!subject.trim() || !message.trim()) {
      toast.error(language === 'fr' ? 'Veuillez remplir tous les champs obligatoires' : 'Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);

      let screenshotUrl: string | null = null;

      // Upload screenshot if provided
      if (screenshot) {
        const fileExt = screenshot.name.split('.').pop();
        const fileName = `feedback/${user?.id || 'anonymous'}/${Date.now()}.${fileExt}`;

        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('user-uploads')
          .upload(fileName, screenshot, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) {
          logger.error('Screenshot upload failed:', uploadError);
          // Continue without screenshot
        } else {
          const { data: { publicUrl } } = supabase.storage
            .from('user-uploads')
            .getPublicUrl(fileName);
          screenshotUrl = publicUrl;
        }
      }

      // Get user's organization if they have one
      let organizationId: string | null = null;
      if (user) {
        const { data: orgMember } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id)
          .maybeSingle();
        organizationId = orgMember?.organization_id || null;
      }

      // Insert feedback into database
      const { data: insertedFeedback, error: insertError } = await supabase
        .from('user_feedback')
        .insert({
          user_id: user?.id || null,
          organization_id: organizationId,
          subject: subject.trim(),
          message: message.trim(),
          feedback_type: feedbackType,
          contact_email: contactEmail.trim() || null,
          contact_name: contactName.trim() || null,
          screenshot_url: screenshotUrl,
          page_url: window.location.href,
          browser_info: {
            userAgent: navigator.userAgent,
            language: navigator.language,
            screenSize: `${window.screen.width}x${window.screen.height}`,
            viewportSize: `${window.innerWidth}x${window.innerHeight}`,
          },
        })
        .select('id')
        .single();

      if (insertError) throw insertError;

      // Send email notification (non-blocking)
      try {
        await supabase.functions.invoke('send-feedback-notification', {
          body: {
            feedbackId: insertedFeedback?.id || 'unknown',
            feedbackType,
            subject: subject.trim(),
            message: message.trim(),
            contactEmail: contactEmail.trim() || undefined,
            contactName: contactName.trim() || undefined,
            contactPhone: contactPhone.trim() || undefined,
            screenshotUrl: screenshotUrl || undefined,
            screenshotBase64: screenshotPreview || undefined, // Embed image directly in email
            pageUrl: window.location.href,
            userEmail: user?.email || undefined,
          },
        });
        logger.info('Feedback notification email sent');
      } catch (emailError) {
        // Don't block success if email fails - feedback is already saved
        logger.warn('Failed to send feedback notification email:', emailError);
      }

      logger.info('Feedback submitted successfully');
      setSuccess(true);

      toast.success(
        language === 'fr' ? 'Message envoyé !' : 'Message sent!',
        { description: language === 'fr' ? 'Nous vous répondrons rapidement.' : 'We\'ll get back to you soon.' }
      );

      // Close modal after short delay
      setTimeout(() => {
        onOpenChange(false);
        resetForm();
      }, 2000);

    } catch (error: any) {
      logger.error('Error submitting feedback:', error);
      toast.error(
        language === 'fr' ? 'Erreur lors de l\'envoi' : 'Error sending message',
        { description: error.message }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    if (!success) {
      // Don't reset if just viewing success state
      setTimeout(resetForm, 300);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        {success ? (
          // Success state
          <div className="py-8 text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <DialogTitle className="text-xl mb-2">
              {language === 'fr' ? 'Message envoyé !' : 'Message sent!'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr'
                ? 'Merci pour votre retour. Notre équipe vous répondra sous 24-48h.'
                : 'Thank you for your feedback. Our team will respond within 24-48 hours.'}
            </DialogDescription>
          </div>
        ) : (
          // Form state
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-primary" />
                {language === 'fr' ? 'Nous contacter' : 'Contact us'}
              </DialogTitle>
              <DialogDescription>
                {language === 'fr'
                  ? 'Un bug ? Une idée ? Une question ? Nous sommes là pour vous aider.'
                  : 'A bug? An idea? A question? We\'re here to help.'}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              {/* Feedback Type */}
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Type de message' : 'Message type'}</Label>
                <div className="grid grid-cols-2 gap-2">
                  {(Object.entries(feedbackTypeConfig) as [FeedbackType, typeof feedbackTypeConfig[FeedbackType]][]).map(([type, config]) => {
                    const Icon = config.icon;
                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setFeedbackType(type)}
                        className={cn(
                          "flex items-center gap-2 p-3 rounded-lg border transition-all text-left",
                          feedbackType === type
                            ? "border-primary bg-primary/5 ring-1 ring-primary"
                            : "border-border hover:border-primary/50"
                        )}
                      >
                        <Icon className={cn("w-4 h-4", config.color)} />
                        <span className="text-sm font-medium">
                          {language === 'fr' ? config.label : config.labelEn}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Subject */}
              <div className="space-y-2">
                <Label htmlFor="subject">
                  {language === 'fr' ? 'Sujet' : 'Subject'} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                />
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="message">
                  {language === 'fr' ? 'Message' : 'Message'} <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                  required
                />
              </div>

              {/* Screenshot Upload */}
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Capture d\'écran (optionnel)' : 'Screenshot (optional)'}</Label>
                {screenshotPreview ? (
                  <div className="relative rounded-lg border overflow-hidden">
                    <img
                      src={screenshotPreview}
                      alt="Screenshot preview"
                      className="w-full h-32 object-cover"
                    />
                    <button
                      type="button"
                      onClick={removeScreenshot}
                      className="absolute top-2 right-2 p-1 bg-black/50 rounded-full hover:bg-black/70 transition-colors"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                  </div>
                ) : (
                  <div
                    onDrop={handleDrop}
                    onDragOver={(e) => e.preventDefault()}
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
                  >
                    <ImageIcon className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      {language === 'fr'
                        ? 'Cliquez ou glissez une image ici'
                        : 'Click or drag an image here'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      PNG, JPG (max 5 Mo)
                    </p>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="contactName">
                    {language === 'fr' ? 'Votre nom' : 'Your name'}
                  </Label>
                  <Input
                    id="contactName"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    placeholder="Jean Dupont"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contactEmail">
                    {language === 'fr' ? 'Votre email' : 'Your email'}
                  </Label>
                  <Input
                    id="contactEmail"
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    placeholder="jean@example.com"
                  />
                </div>
              </div>

              <DialogFooter className="pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={loading}
                >
                  {language === 'fr' ? 'Annuler' : 'Cancel'}
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {language === 'fr' ? 'Envoi...' : 'Sending...'}
                    </>
                  ) : (
                    language === 'fr' ? 'Envoyer' : 'Send'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackModal;
